import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAdminContentDocument,
  updateAdminContentDocument,
  listAdminDemoVideos,
  createAdminDemoVideo,
  updateAdminDemoVideo,
  deleteAdminDemoVideo,
  uploadVideoThumbnail,
} from '../api/admin.js';
import type { ContentSlug, AdminDemoVideo } from '@m8-app/contracts/rest/content.js';

type ActiveTab = ContentSlug | 'demo_videos';

const DOC_TABS: { slug: ContentSlug; label: string }[] = [
  { slug: 'privacy_policy', label: 'Privacy Policy' },
  { slug: 'terms_of_use', label: 'Terms of Use' },
  { slug: 'about_app', label: 'About App' },
];

const LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
];

const emptyDocTrans = { title: '', bodyHtml: '' };
const emptyVideoTrans = { en: { title: '', description: '' }, hi: { title: '', description: '' } };
const emptyVideoForm = { videoUrl: '', sortOrder: 0, active: true, ...emptyVideoTrans };

// ── Document section ──────────────────────────────────────────────────────────

function DocumentSection({ slug, onDirtyChange }: { slug: ContentSlug; onDirtyChange?: (d: boolean) => void }) {
  const qc = useQueryClient();
  const [activeLang, setActiveLang] = useState<'en' | 'hi'>('en');
  const [form, setForm] = useState<{
    en: typeof emptyDocTrans;
    hi: typeof emptyDocTrans;
    published: boolean;
    externalUrl: string;
  }>({ en: { ...emptyDocTrans }, hi: { ...emptyDocTrans }, published: false, externalUrl: '' });
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['content-doc', slug],
    queryFn: () => getAdminContentDocument(slug),
  });

  // react-query v5 removed useQuery's onSuccess — sync server data into the
  // editable form state here instead, same as onSuccess used to.
  useEffect(() => {
    if (!data) return;
    const en = data.translations.find((t) => t.locale === 'en') ?? emptyDocTrans;
    const hi = data.translations.find((t) => t.locale === 'hi') ?? emptyDocTrans;
    setForm({
      en: { title: en.title, bodyHtml: en.bodyHtml },
      hi: { title: hi.title, bodyHtml: hi.bodyHtml },
      published: data.published,
      externalUrl: data.externalUrl ?? '',
    });
    setDirty(false);
    setErr('');
    setSaved(false);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateAdminContentDocument(slug, {
        published: form.published,
        externalUrl: form.externalUrl.trim() || null,
        translations: [
          { locale: 'en', title: form.en.title, bodyHtml: form.en.bodyHtml },
          { locale: 'hi', title: form.hi.title, bodyHtml: form.hi.bodyHtml },
        ].filter((t) => t.title.trim() && t.bodyHtml.trim()),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content-doc', slug] });
      setDirty(false);
      setErr('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Save failed'),
  });

  function updateField(field: 'title' | 'bodyHtml', value: string) {
    setForm((f) => ({ ...f, [activeLang]: { ...f[activeLang], [field]: value } }));
    setDirty(true);
    setSaved(false);
    onDirtyChange?.(true);
  }

  if (isLoading) return <p className="text-sm text-gray-400">Loading…</p>;
  if (isError) return <p className="text-sm text-red-600">Failed to load document. Try refreshing.</p>;

  function handleSave(e: FormEvent) {
    e.preventDefault();
    const hasContent = (form.en.title.trim() && form.en.bodyHtml.trim()) ||
                       (form.hi.title.trim() && form.hi.bodyHtml.trim());
    if (!hasContent) {
      setErr('At least one language must have both a title and body before saving.');
      return;
    }
    const url = form.externalUrl.trim();
    if (url && !/^https?:\/\/\S+$/i.test(url)) {
      setErr('Link must be a full URL starting with http:// or https://');
      return;
    }
    setErr('');
    saveMutation.mutate();
  }

  return (
    <form onSubmit={handleSave}>
      <div className="flex items-center justify-between mb-5">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => { setForm((f) => ({ ...f, published: e.target.checked })); setDirty(true); }}
            className="w-4 h-4 accent-indigo-600"
          />
          Published (visible in mobile app)
        </label>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-green-600 font-medium">✓ Saved</span>}
          {err && <span className="text-xs text-red-600">{err}</span>}
          <button
            type="submit"
            disabled={saveMutation.isPending || !dirty}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-40"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
        <label htmlFor="doc-external-url" className="block text-xs font-medium text-gray-500 mb-1">
          Link (optional, same for all languages)
        </label>
        <input
          id="doc-external-url"
          type="url"
          value={form.externalUrl}
          onChange={(e) => {
            const v = e.target.value;
            setForm((f) => ({ ...f, externalUrl: v }));
            setDirty(true);
            setSaved(false);
            onDirtyChange?.(true);
          }}
          placeholder="https://www.example.com/privacy-policy"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <p className="text-xs text-gray-400 mt-1">
          Shown in the app under the text as a tappable &ldquo;view the full document online&rdquo; line — for example the policy page on your website. Leave empty to show text only.
        </p>
      </div>

      <div className="flex gap-1 mb-4">
        {LOCALES.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            onClick={() => setActiveLang(code as 'en' | 'hi')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeLang === code ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Title ({activeLang === 'en' ? 'English' : 'Hindi'})
          </label>
          <input
            value={form[activeLang].title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder={activeLang === 'en' ? 'Privacy Policy' : 'गोपनीयता नीति'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Body HTML ({activeLang === 'en' ? 'English' : 'Hindi'})
          </label>
          <textarea
            value={form[activeLang].bodyHtml}
            onChange={(e) => updateField('bodyHtml', e.target.value)}
            rows={20}
            placeholder="<h2>Section</h2><p>Content here…</p>"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
          />
          <p className="text-xs text-gray-400 mt-1">
            Enter HTML. Supported: &lt;h1&gt; &lt;h2&gt; &lt;p&gt; &lt;ul&gt; &lt;li&gt; &lt;strong&gt; &lt;em&gt; &lt;a&gt;
          </p>
        </div>
      </div>
    </form>
  );
}

// ── Demo Videos section ───────────────────────────────────────────────────────

function DemoVideosSection({ onDirtyChange }: { onDirtyChange?: (d: boolean) => void }) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [activeLang, setActiveLang] = useState<'en' | 'hi'>('en');
  const [form, setForm] = useState(emptyVideoForm);
  const [err, setErr] = useState('');
  const [thumbErr, setThumbErr] = useState<Record<string, string>>({});
  const [thumbUploadingId, setThumbUploadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['demo-videos-admin'],
    queryFn: listAdminDemoVideos,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createAdminDemoVideo({
        videoUrl: form.videoUrl,
        sortOrder: form.sortOrder,
        active: form.active,
        translations: [
          { locale: 'en', title: form.en.title, description: form.en.description || null },
          { locale: 'hi', title: form.hi.title, description: form.hi.description || null },
        ].filter((t) => t.title.trim()),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demo-videos-admin'] });
      setShowAdd(false);
      setForm(emptyVideoForm);
      setErr('');
    },
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Failed'),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateAdminDemoVideo(editingId!, {
        videoUrl: form.videoUrl || undefined,
        sortOrder: form.sortOrder,
        active: form.active,
        translations: [
          { locale: 'en', title: form.en.title, description: form.en.description || null },
          { locale: 'hi', title: form.hi.title, description: form.hi.description || null },
        ].filter((t) => t.title.trim()),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demo-videos-admin'] });
      setEditingId(null);
      setErr('');
    },
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAdminDemoVideo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['demo-videos-admin'] }),
  });

  const thumbMutation = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const base64 = await readFileAsBase64(file);
      await uploadVideoThumbnail(id, { base64, mimeType: file.type });
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['demo-videos-admin'] });
      setThumbErr((prev) => ({ ...prev, [id]: '' }));
      setThumbUploadingId(null);
    },
    onError: (e: unknown, { id }) => {
      setThumbErr((prev) => ({ ...prev, [id]: (e as { message?: string }).message ?? 'Upload failed' }));
      setThumbUploadingId(null);
    },
  });

  function readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function startEdit(video: AdminDemoVideo) {
    setEditingId(video.id);
    setShowAdd(false);
    const en = video.translations.find((t) => t.locale === 'en');
    const hi = video.translations.find((t) => t.locale === 'hi');
    setForm({
      videoUrl: video.videoUrl,
      sortOrder: video.sortOrder,
      active: video.active,
      en: { title: en?.title ?? '', description: en?.description ?? '' },
      hi: { title: hi?.title ?? '', description: hi?.description ?? '' },
    });
    setActiveLang('en');
    setErr('');
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (editingId) updateMutation.mutate();
    else createMutation.mutate();
  }

  const videos = data?.videos ?? [];
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <p className="text-sm text-gray-500">
          Manage demo videos shown in the mobile app — bilingual titles, thumbnail, and video URL.
        </p>
        <button
          onClick={() => { setShowAdd(true); setEditingId(null); setForm(emptyVideoForm); setActiveLang('en'); setErr(''); onDirtyChange?.(true); }}
          className="ml-4 shrink-0 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700"
        >
          + Add Video
        </button>
      </div>

      {(showAdd || editingId) && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 max-w-3xl">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">{editingId ? 'Edit Video' : 'Add Video'}</h3>

          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">Video URL</label>
            <input
              value={form.videoUrl}
              onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
              placeholder="https://cdn.example.com/demo.mp4"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              required
            />
          </div>

          <div className="flex gap-1 mb-3">
            {LOCALES.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                onClick={() => setActiveLang(code as 'en' | 'hi')}
                className={`px-3 py-1 rounded text-xs font-medium ${
                  activeLang === code ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Title ({activeLang === 'en' ? 'English' : 'Hindi'}) *
            </label>
            <input
              value={form[activeLang].title}
              onChange={(e) => setForm({ ...form, [activeLang]: { ...form[activeLang], title: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              required={activeLang === 'en'}
            />
          </div>

          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Description ({activeLang === 'en' ? 'English' : 'Hindi'}) — optional
            </label>
            <textarea
              value={form[activeLang].description}
              onChange={(e) => setForm({ ...form, [activeLang]: { ...form[activeLang], description: e.target.value } })}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            />
          </div>

          <div className="flex items-center gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sort order</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value, 10) || 0 })}
                className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Active (visible in app)
            </label>
          </div>

          {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isPending}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-40"
            >
              {isPending ? 'Saving…' : editingId ? 'Save' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setEditingId(null); setErr(''); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <p className="px-4 py-4 text-sm text-gray-400">Loading…</p>
        ) : isError ? (
          <p className="px-4 py-4 text-sm text-red-600">Couldn't load videos. Check your connection and try again.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium w-16">Order</th>
                  <th className="px-4 py-3 font-medium w-20">Thumbnail</th>
                  <th className="px-4 py-3 font-medium">Title (EN)</th>
                  <th className="px-4 py-3 font-medium">Title (HI)</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {videos.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-400">{v.sortOrder}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        {v.thumbnailUrl ? (
                          <img
                            src={v.thumbnailUrl}
                            alt="thumb"
                            className="w-14 h-10 object-cover rounded border border-gray-200"
                          />
                        ) : (
                          <div className="w-14 h-10 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-300 text-xs">
                            none
                          </div>
                        )}
                        <label className="text-xs text-indigo-600 hover:underline cursor-pointer">
                          {thumbUploadingId === v.id ? 'Uploading…' : 'Upload'}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            ref={thumbUploadingId === v.id ? fileInputRef : undefined}
                            onChange={(e) => {
                              const file = e.target.files?.[0] ?? null;
                              if (!file) return;
                              setThumbUploadingId(v.id);
                              thumbMutation.mutate({ id: v.id, file });
                            }}
                          />
                        </label>
                        {thumbErr[v.id] && <p className="text-xs text-red-500">{thumbErr[v.id]}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate">
                      {v.translations.find((t) => t.locale === 'en')?.title ?? v.title}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-gray-500">
                      {v.translations.find((t) => t.locale === 'hi')?.title ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        v.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {v.active ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-3">
                      <button onClick={() => startEdit(v)} className="text-xs text-indigo-600 hover:underline">
                        Edit
                      </button>
                      <button
                        onClick={() => { if (confirm('Delete this video?')) deleteMutation.mutate(v.id); }}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {videos.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      No demo videos yet — add one above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

const ALL_TABS: { id: ActiveTab; label: string }[] = [
  { id: 'privacy_policy', label: 'Privacy Policy' },
  { id: 'terms_of_use', label: 'Terms of Use' },
  { id: 'about_app', label: 'About App' },
  { id: 'demo_videos', label: 'Demo Videos' },
];

export function ContentDocuments() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('privacy_policy');
  const [dirty, setDirty] = useState(false);

  function switchTab(id: ActiveTab) {
    if (dirty && !confirm('You have unsaved changes. Discard and switch?')) return;
    setActiveTab(id);
    setDirty(false);
  }

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Content Management</h2>
        <p className="text-sm text-gray-500">
          Manage Privacy Policy, Terms of Use, About App, and Demo Videos — changes appear in the mobile app instantly.
        </p>
      </div>

      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {ALL_TABS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => switchTab(id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'demo_videos' ? (
        <DemoVideosSection onDirtyChange={setDirty} />
      ) : (
        <DocumentSection key={activeTab} slug={activeTab} onDirtyChange={setDirty} />
      )}
    </div>
  );
}
